import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import supabase from "../../supabaseClient/supabaseClient";
import {
  EMAIL_CONFIRMED_PATH,
  clearPendingSignupEmail,
  getPendingSignupEmail,
  readAuthCallbackParams,
  resendConfirmationEmail,
} from "./emailConfirmation";
import "./loginPage.css";
import Logo from "../Navbar/sheep.svg";

export const REDIRECT_DELAY_MS = 2500;

const describeFailure = (params, initError) => {
  if (params.error_code === "otp_expired") {
    return "This confirmation link has expired or has already been used.";
  }
  if (params.error_description) {
    return `This confirmation link didn't work: ${params.error_description}`;
  }
  if (initError?.message) {
    return `This confirmation link didn't work: ${initError.message}`;
  }
  return "We couldn't sign you in from this link. If you've already confirmed your email, please sign in.";
};

const ConfirmEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Read the link's result before the address bar is cleaned up.
  const [urlParams] = useState(() => readAuthCallbackParams(location));
  const [status, setStatus] = useState("checking");
  const [failureMessage, setFailureMessage] = useState("");
  const [email, setEmail] = useState(getPendingSignupEmail);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    let cancelled = false;
    let redirectTimer;

    const confirm = async () => {
      // Supabase parses the link's tokens/errors from the URL while initialising.
      const { error: initError } = await supabase.auth.initialize();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;

      navigate(EMAIL_CONFIRMED_PATH, { replace: true });

      const linkFailed = Boolean(urlParams.error || urlParams.error_code || urlParams.error_description);
      if (session && !linkFailed) {
        clearPendingSignupEmail();
        setStatus("confirmed");
        redirectTimer = setTimeout(() => navigate("/create", { replace: true }), REDIRECT_DELAY_MS);
        return;
      }

      setFailureMessage(describeFailure(urlParams, initError));
      setStatus("failed");
    };

    confirm();

    return () => {
      cancelled = true;
      clearTimeout(redirectTimer);
    };
  }, [navigate, urlParams]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResendMessage({ text: "Please enter a valid email address.", type: "error" });
      return;
    }

    setResending(true);
    try {
      const { error } = await resendConfirmationEmail(email);
      if (error) throw error;
      setResendMessage({
        text: `We've sent a new confirmation link to ${email}. It may take a minute to arrive — check your spam folder too.`,
        type: "success",
      });
    } catch (error) {
      setResendMessage({ text: error.message, type: "error" });
    } finally {
      setResending(false);
    }
  };

  if (status === "checking") {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading-spinner"></div>
          <p className="confirm-text">Confirming your email…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="brand-content">
          <div className="logo-container">
            <img src={Logo} alt="Worksheep Logo" className="worksheep-logo" />
          </div>
        </div>

        {status === "confirmed" ? (
          <div className="auth-form">
            <h2>Email confirmed — you're signed in</h2>
            <p className="confirm-text">Taking you to Worksheep…</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/create", { replace: true })}
            >
              Start creating worksheets
            </button>
          </div>
        ) : (
          <div className="auth-form">
            <h2>We couldn't confirm your email</h2>
            <div className="message error">{failureMessage}</div>

            <form onSubmit={handleResend}>
              <p className="confirm-text">Enter your email and we'll send you a new confirmation link.</p>
              <div className="form-group">
                <label htmlFor="resend-email">Email Address</label>
                <input
                  id="resend-email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>

              {resendMessage.text && (
                <div className={`message ${resendMessage.type}`}>{resendMessage.text}</div>
              )}

              <button type="submit" disabled={resending} className="btn btn-success">
                {resending ? "Sending..." : "Resend confirmation email"}
              </button>
            </form>

            <button
              type="button"
              className="btn btn-link"
              onClick={() => navigate("/login", { replace: true })}
            >
              Already confirmed? Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmail;
